// Le collecteur de signaux : la matière première des algorithmes.
//
// `js/algorithmes.js` ne sait rien lire — c'est ici qu'on rassemble ce qu'il lui
// faut, une seule fois, et qu'on le tient à jour. Les écrans ne s'abonnent donc
// pas chacun de leur côté aux mêmes données : ils demandent un instantané.
//
// Deux signaux sont gardés sur l'appareil et non en base, parce qu'ils ne
// regardent que ce membre-ci sur ce téléphone-ci :
//   • les publications déjà vues  → pour ne pas remettre la même en tête ;
//   • les suggestions écartées    → pour ne pas reproposer un « non ».
// Conséquence assumée : changer de téléphone les remet à zéro.

import Store from "./store.js";

const CLE_VUS = "koinonia:vus";
const CLE_IGNORES = "koinonia:suggestions-ecartees";
const MAX_VUS = 400; // au-delà, on oublie les plus anciennes

/* ─────────────────────────────────────────────── mémoire locale de l'appareil */

function lireLocal(cle, uid) {
  try {
    return JSON.parse(localStorage.getItem(`${cle}:${uid}`) || "[]");
  } catch {
    return [];
  }
}

function ecrireLocal(cle, uid, tableau) {
  try {
    localStorage.setItem(`${cle}:${uid}`, JSON.stringify(tableau));
  } catch {
    /* stockage plein ou refusé : le classement fonctionne sans, en moins fin */
  }
}

/* ─────────────────────────────────────────────────────────── le collecteur */

/**
 * Ouvre la collecte pour le membre connecté.
 * @returns {{instantane: function, surChangement: function, marquerVu: function,
 *            ecarter: function, rafraichirVoisinage: function, detruire: function}}
 */
export function creerSignaux(moi) {
  const uid = moi.uid;

  // état brut, alimenté par les abonnements
  let relations = [];
  let mesAmens = new Set();
  let convos = [];
  let notifs = [];
  let mesGroupes = [];
  let mesPages = [];
  let bloques = new Set();
  let enLigne = new Set();
  let vuA = new Map(); // uid -> instant de la dernière présence connue

  // état dérivé, recalculé par le voisinage (asynchrone)
  let amisDeMesAmis = new Map();
  /* ⚠️ DEUX CARTES, ET CE N'EST PAS UN DOUBLON.
     `amisDeMesAmis` sert à SUGGÉRER : il écarte donc ceux qui sont déjà mes
     amis, puisqu'on ne propose pas quelqu'un qu'on connaît déjà.
     `amisEnCommun` sert à AFFICHER « 3 amis en commun » sur un profil — et là,
     la question se pose surtout pour un ami. Sans cette seconde carte, la
     mention restait vide précisément sur les profils où elle a un sens, ce que
     Josaphat a vu le 14 août 2026 sur le profil d'un de ses amis.
     Elles se remplissent dans la même boucle : le voisinage n'est lu qu'une
     fois, et la seconde carte ne coûte rien de plus. */
  let amisEnCommun = new Map();
  let espacesCommuns = new Map();

  // ⚠️ Ces deux drapeaux sont déclarés ICI, avant les abonnements, et non à côté
  // de la fonction qui les utilise. Les abonnements de Koinonia émettent DÈS
  // leur installation, et leurs rappels appellent `rafraichirVoisinage()` — donc
  // pendant que `creerSignaux` court encore. Déclarés plus bas, ils étaient
  // inaccessibles à cet instant et le premier rafraîchissement du voisinage
  // échouait en silence : plus aucun « ami en commun » dans les suggestions,
  // sans le moindre message.
  let voisinageEnCours = false;
  let voisinageAttendu = false;
  const membres = new Map(); // annuaire connu, rempli au fil des écrans
  const auteursDesPosts = new Map(); // postId -> authorId, pour attribuer mes amens

  const vus = new Set(lireLocal(CLE_VUS, uid));
  const ignores = new Set(lireLocal(CLE_IGNORES, uid));

  const auditeurs = new Set();
  let minuteur;
  function prevenir() {
    clearTimeout(minuteur);
    // les abonnements se déclenchent en rafale : on ne recalcule qu'une fois
    minuteur = setTimeout(() => auditeurs.forEach((f) => f()), 60);
  }

  /* ---------------------------------------------------------- abonnements */

  const arrets = [
    Store.amis.subscribe((rs) => {
      relations = rs || [];
      prevenir();
      rafraichirVoisinage(); // le cercle a changé : les amis d'amis aussi
    }),
    Store.amens.subscribeMine((set) => {
      mesAmens = set || new Set();
      prevenir();
    }),
    Store.convos.subscribe((cs) => {
      convos = cs || [];
      prevenir();
    }),
    Store.notifs.subscribe((ns) => {
      notifs = ns || [];
      prevenir();
    }),
    Store.groupes.subscribeMiens((gs) => {
      mesGroupes = gs || [];
      prevenir();
      rafraichirVoisinage();
    }),
    Store.pages.subscribeMiennes((ps) => {
      mesPages = ps || [];
      prevenir();
      rafraichirVoisinage();
    }),
    Store.blocages.subscribeMiens((ids) => {
      bloques = new Set(ids || []);
      prevenir();
    }),
    Store.presence.subscribe((etat) => {
      // Le magasin rend désormais { enLigne, vuA }. On tolère l'ancienne forme
      // — un simple Set — pour qu'un pilote non encore mis à jour ne fasse pas
      // tomber la présence entière.
      enLigne = etat?.enLigne || (etat instanceof Set ? etat : new Set());
      vuA = etat?.vuA || new Map();
      prevenir();
    }),
    // le fil sert aussi à savoir qui a écrit quoi : sans cela, un amen ne
    // pourrait pas être attribué à un auteur
    Store.posts.subscribe((posts) => {
      apprendrePosts(posts);
      prevenir();
    }),
  ];

  /* ------------------------------------------------------------ voisinage */

  /**
   * Va chercher ce qui ne tient pas dans un abonnement : les amis de mes amis,
   * et les membres qui partagent un groupe ou une page avec moi.
   * Coûteux : on ne le relance pas plus d'une fois à la fois.
   */
  async function rafraichirVoisinage() {
    if (voisinageEnCours) {
      voisinageAttendu = true;
      return;
    }
    voisinageEnCours = true;
    try {
      const mesAmis = [...idsAmis()];
      const [carte, co] = await Promise.all([
        mesAmis.length ? Store.signaux.amisDe(mesAmis) : Promise.resolve(new Map()),
        Store.signaux.coMembres(),
      ]);

      // amis d'amis : on compte combien de mes amis connaissent chaque personne
      const compte = new Map();
      const tous = new Map(); // le même décompte, mes amis compris
      const dejaAmi = idsAmis();
      for (const [, leursAmis] of carte) {
        for (const autre of leursAmis) {
          if (autre === uid) continue;
          tous.set(autre, (tous.get(autre) || 0) + 1);
          if (dejaAmi.has(autre)) continue;
          compte.set(autre, (compte.get(autre) || 0) + 1);
        }
      }
      amisDeMesAmis = compte;
      amisEnCommun = tous;
      espacesCommuns = co || new Map();
      prevenir();
    } catch (e) {
      // un voisinage indisponible ne doit jamais casser un écran :
      // le classement retombe sur les signaux directs
      console.warn("Voisinage indisponible :", e?.message || e);
    } finally {
      voisinageEnCours = false;
      if (voisinageAttendu) {
        voisinageAttendu = false;
        rafraichirVoisinage();
      }
    }
  }

  /* ------------------------------------------------------------- dérivés */

  function idsAmis() {
    return new Set(
      relations.filter((r) => r.etat === "acceptee").map((r) => r.membres.find((m) => m !== uid)).filter(Boolean)
    );
  }

  function idsEnAttente() {
    return new Set(
      relations.filter((r) => r.etat === "attente").map((r) => r.membres.find((m) => m !== uid)).filter(Boolean)
    );
  }

  /** Qui j'ai soutenu, et combien de fois : mes amens rapportés à leurs auteurs. */
  function auteursSoutenus() {
    const m = new Map();
    for (const postId of mesAmens) {
      const auteur = auteursDesPosts.get(postId);
      if (auteur && auteur !== uid) m.set(auteur, (m.get(auteur) || 0) + 1);
    }
    return m;
  }

  /**
   * Qui réagit à ce que j'écris. Mes notifications sont exactement ce journal :
   * chacune porte l'auteur du geste. Pas besoin d'une requête de plus.
   */
  function interactionsRecues() {
    const m = new Map();
    for (const n of notifs) {
      if (!n.deId || n.deId === uid) continue;
      if (!["amen", "priere", "commentaire", "partage"].includes(n.type)) continue;
      m.set(n.deId, (m.get(n.deId) || 0) + 1);
    }
    return m;
  }

  /** Avec qui j'échange, et quand pour la dernière fois. */
  function echanges() {
    const m = new Map();
    for (const c of convos) {
      const autre = (c.membres || []).find((x) => x !== uid);
      if (!autre) continue;
      m.set(autre, { nombre: c.dernier ? 1 : 0, dernier: c.dernierAt || 0 });
    }
    return m;
  }

  function apprendrePosts(posts) {
    for (const p of posts || []) {
      if (p?.id && p.authorId) auteursDesPosts.set(p.id, p.authorId);
    }
  }

  /* ---------------------------------------------------------------- API */

  return {
    /** Photographie de l'instant, à passer aux algorithmes. */
    instantane() {
      return {
        moi: { uid, eglise: moi.eglise || "", ville: moi.ville || "", createdAt: moi.createdAt || 0 },
        amis: idsAmis(),
        enAttente: idsEnAttente(),
        amisDeMesAmis,
        amisEnCommun,
        espacesCommuns,
        auteursSoutenus: auteursSoutenus(),
        interactionsRecues: interactionsRecues(),
        echanges: echanges(),
        mesGroupes: new Set(mesGroupes.map((g) => g.id)),
        mesPages: new Set(mesPages.map((p) => p.id)),
        bloques,
        enLigne,
        vuA,
        vus,
        ignores,
        membres,
        maintenant: Date.now(),
      };
    },

    /** Prévenu à chaque évolution d'un signal. Rend la fonction d'arrêt. */
    surChangement(cb) {
      auditeurs.add(cb);
      return () => auditeurs.delete(cb);
    },

    /** Complète l'annuaire connu : plus il est fourni, plus le classement est fin. */
    apprendreMembres(liste) {
      for (const u of liste || []) if (u?.uid) membres.set(u.uid, u);
    },

    apprendrePosts,

    /** Une publication réellement passée sous les yeux du membre. */
    marquerVu(postId) {
      if (!postId || vus.has(postId)) return;
      vus.add(postId);
      const tableau = [...vus].slice(-MAX_VUS);
      vus.clear();
      tableau.forEach((x) => vus.add(x));
      ecrireLocal(CLE_VUS, uid, tableau);
    },

    /** « Ne plus me proposer cette personne. » */
    ecarter(autreUid) {
      if (!autreUid) return;
      ignores.add(autreUid);
      ecrireLocal(CLE_IGNORES, uid, [...ignores]);
      prevenir();
    },

    rafraichirVoisinage,

    detruire() {
      clearTimeout(minuteur);
      auditeurs.clear();
      arrets.forEach((f) => f && f());
    },
  };
}
